import { UpdateMerchantBusinessPayload } from "@/models/payload/profile";
import { Loader, Select, Skeleton, Textarea, TextInput } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCamera } from "@tabler/icons-react";
import imageCompression from "browser-image-compression";
import { useEffect, useRef, useState } from "react";
import ImageUploading, { ImageListType, ImageType } from "react-images-uploading";
import * as Yup from "yup";
import SettingsShell, { SettingsSection } from "@/modules/settings/SettingsShell";
import classes from "@/modules/settings/settings.module.css";
import { useUpdateMerchantProfileImage } from "@/services/merchant/utilities";
import { useGetImage } from "@/utilities/helper/getImage";
import useGetMerchant from "../../hooks/useGetMerchant";
import useGetMerchantTypes from "../../hooks/useGetMerchantType";
import useUpdateMerchantBusiness from "../../hooks/useUpdateMerchantBusiness";

const schema = Yup.object({
  merchantTypeId: Yup.number().required("نوع کسب‌وکار را انتخاب کنید"),
  title: Yup.string().required("نام مجموعه را بنویسید"),
});
const DESCRIPTION_MAX = 1000;

type Values = Pick<UpdateMerchantBusinessPayload, "title" | "description" | "merchantTypeId" | "theme">;

// «پروفایل سالن»: logo, name, type and the about text (the site colour lives on «قالب و رنگ سایت»). Only these are sent, so the SEO
// fields saved on «سایت و سئو» are left as they are.
function EditMerchantProfile() {
  const { data, isLoading, refetch } = useGetMerchant();
  const { data: merchantTypes, isLoading: typesLoading } = useGetMerchantTypes();
  const { updateMerchant, isLoading: saving } = useUpdateMerchantBusiness();

  const initial = (): Values => ({
    title: data?.businessInfo?.title ?? "",
    description: data?.businessInfo?.description ?? "",
    merchantTypeId: data?.businessInfo?.merchantType?.id?.toString(),
    theme: data?.businessInfo?.theme,
  });
  const form = useForm<Values>({ validate: yupResolver(schema), initialValues: initial() });
  useEffect(() => {
    const values = initial();
    form.setValues(values);
    form.resetDirty(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const submit = (values: Values) =>
    updateMerchant(
      { ...values, description: values.description ?? "", subDomain: data?.businessInfo?.subDomain || undefined, voiceType: data?.businessInfo?.voiceType },
      { onSuccess() { notifications.show({ message: "تغییرات ذخیره شد", color: "green" }); form.resetDirty(); refetch(); } },
    );

  // Logo: uploads as soon as it is picked.
  const { imageUrl } = useGetImage({ image: data?.logo, bucket: "merchant" });
  const [logo, setLogo] = useState<ImageType[]>([]);
  const prefilled = useRef(false);
  const [uploading, setUploading] = useState(false);
  const { mutate: uploadLogo } = useUpdateMerchantProfileImage();
  useEffect(() => {
    if (!prefilled.current && imageUrl) {
      setLogo([{ data_url: imageUrl, file: undefined }]);
      prefilled.current = true;
    }
  }, [imageUrl]);
  const onLogo = async (list: ImageListType) => {
    const file = list[0]?.file;
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      notifications.show({ message: "لطفاً تصویر JPG، PNG یا WebP انتخاب کنید.", color: "red" });
      return;
    }
    const previous = logo;
    setLogo(list);
    setUploading(true);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1000, useWebWorker: true, fileType: file.type || "image/jpeg", initialQuality: 0.8 });
      uploadLogo(new File([compressed], `logo.${file.name.split(".").pop() || "jpg"}`, { type: file.type || "image/jpeg" }), {
        onSuccess: () => { notifications.show({ message: "لوگو به‌روز شد", color: "green" }); refetch(); setUploading(false); },
        onError: () => { notifications.show({ message: "بارگذاری لوگو انجام نشد", color: "red" }); setLogo(previous); setUploading(false); },
      });
    } catch {
      notifications.show({ message: "پردازش تصویر انجام نشد", color: "red" });
      setLogo(previous);
      setUploading(false);
    }
  };

  const description = form.values.description ?? "";

  return (
    <SettingsShell title="پروفایل سالن" description="نام، لوگو و معرفی سالن روی سایت رزرو و در پیامک‌ها." onSubmit={form.onSubmit(submit)} dirty={form.isDirty()} saving={saving} onReset={() => form.reset()}>
      {isLoading ? (
        <><Skeleton height={120} radius={14} /><Skeleton height={180} radius={14} /></>
      ) : (
        <>
          <SettingsSection title="لوگو" description="تصویر مربعی، روی سایت و در نتیجه‌ی جستجو کنار نام سالن.">
            <div className={classes.logoRow}>
              <ImageUploading value={logo} onChange={onLogo} maxNumber={1} dataURLKey="data_url" acceptType={["jpg", "jpeg", "png", "webp"]}>
                {({ onImageUpload, dragProps }) => (
                  <button type="button" className={classes.logo} onClick={onImageUpload} {...dragProps} aria-label="تغییر لوگو">
                    {logo[0]?.data_url ? <img src={logo[0].data_url} alt="لوگوی مجموعه" /> : <IconCamera size={26} stroke={1.5} />}
                    {uploading && <span className={classes.busy} style={{ borderRadius: "50%" }}><Loader size="sm" color="white" /></span>}
                  </button>
                )}
              </ImageUploading>
              <p className={classes.hint}>برای تغییر روی تصویر بزنید. JPG، PNG یا WebP.</p>
            </div>
          </SettingsSection>

          <SettingsSection title="نام و نوع" description="همان نامی که مشتری‌ها سالن را با آن می‌شناسند.">
            <div className={classes.fields}>
              <TextInput label="نام مجموعه" withAsterisk placeholder="مثلاً سالن زیبایی نگین" {...form.getInputProps("title")} />
              <Select
                label="نوع کسب‌وکار"
                withAsterisk
                placeholder="انتخاب کنید"
                searchable
                nothingFoundMessage="پیدا نشد"
                disabled={typesLoading}
                data={merchantTypes?.map((x) => ({ value: x.id?.toString() || "", label: x.title || "" })) || []}
                {...form.getInputProps("merchantTypeId")}
              />
            </div>
          </SettingsSection>

          <SettingsSection title="معرفی" description="سابقه، تخصص‌ها و حال‌وهوای سالن؛ روی سایت رزرو نشان داده می‌شود.">
            <Textarea aria-label="معرفی سالن" autosize minRows={4} maxRows={10} maxLength={DESCRIPTION_MAX} placeholder="مثلاً: سالن نگین از ۱۳۹۵ با تمرکز بر رنگ و لایت مو…" {...form.getInputProps("description")} />
            <span className={classes.count}>{description.length.toLocaleString("fa-IR")} از {DESCRIPTION_MAX.toLocaleString("fa-IR")}</span>
          </SettingsSection>

        </>
      )}
    </SettingsShell>
  );
}

export default EditMerchantProfile;
